import { useEffect, useState } from 'react';
import { Users, PlusCircle, Trash2, AlertCircle } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { onboardingApi, ApiError } from '@solara/api-client';
import type { UboDto } from '@solara/api-client';

export default function UboPage() {
  const { t } = useTranslation('p1');
  const [ubos, setUbos] = useState<UboDto[]>([]);
  const [validation, setValidation] = useState<{ valid: boolean; sum: number; count: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Partial<UboDto>>({});

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [list, v] = await Promise.all([onboardingApi.listUbos(), onboardingApi.validateUbo()]);
      setUbos(list);
      setValidation(v);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function addUbo() {
    if (!draft.fullName || !draft.nationality || !draft.dateOfBirth || !draft.residenceAddress || !draft.ownershipPercentage) {
      setError(t('ubo.missingFields'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onboardingApi.createUbo({
        fullName: draft.fullName,
        nationality: draft.nationality,
        dateOfBirth: draft.dateOfBirth,
        residenceAddress: draft.residenceAddress,
        ownershipPercentage: Number(draft.ownershipPercentage),
      });
      setDraft({});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('ubo.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function removeUbo(id: string) {
    setBusy(true);
    try {
      await onboardingApi.deleteUbo(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('ubo.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('ubo.title')}</h1>
        <p className="text-fg-muted text-sm">{t('ubo.subtitle')}</p>
      </header>

      {validation && (
        <Card className="rounded-2xl">
          <CardBody className="flex items-center justify-between text-sm">
            <span>
              {t('ubo.summary', { count: validation.count, sum: validation.sum.toFixed(2) })}
            </span>
            <span className={validation.valid ? 'text-success' : 'text-danger'}>
              {validation.valid ? t('ubo.valid') : t('ubo.invalid')}
            </span>
          </CardBody>
        </Card>
      )}

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('ubo.registeredOwners')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="divide-y divide-border">
            {ubos.length === 0 && (
              <li className="py-3 text-sm text-fg-muted">{t('ubo.noUbos')}</li>
            )}
            {ubos.map((u) => (
              <li key={u.id} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <p className="font-medium">{u.fullName}</p>
                  <p className="text-xs text-fg-muted">
                    {u.nationality} · {u.ownershipPercentage}% · {t('ubo.dateOfBirth')}{' '}
                    {new Date(u.dateOfBirth).toLocaleDateString()}
                  </p>
                </div>
                <Button variant="ghost" onClick={() => void removeUbo(u.id)} disabled={busy}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlusCircle className="h-4 w-4" />
            {t('ubo.addUbo')}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Input
            placeholder={t('ubo.fullName')}
            value={draft.fullName ?? ''}
            onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
          />
          <Input
            placeholder={t('ubo.nationalityHint')}
            value={draft.nationality ?? ''}
            onChange={(e) => setDraft({ ...draft, nationality: e.target.value })}
          />
          <Input
            type="date"
            placeholder={t('ubo.dateOfBirth')}
            value={draft.dateOfBirth ?? ''}
            onChange={(e) => setDraft({ ...draft, dateOfBirth: e.target.value })}
          />
          <Input
            placeholder={t('ubo.residenceAddress')}
            value={draft.residenceAddress ?? ''}
            onChange={(e) => setDraft({ ...draft, residenceAddress: e.target.value })}
          />
          <Input
            type="number"
            placeholder={t('ubo.ownership')}
            min={25}
            max={100}
            value={draft.ownershipPercentage ?? ''}
            onChange={(e) => setDraft({ ...draft, ownershipPercentage: Number(e.target.value) })}
          />
          <div className="sm:col-span-2">
            <Button onClick={addUbo} disabled={busy}>
              {busy ? t('actions.saving') : t('ubo.addUbo')}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
